import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "components/ui/dialog";
import {
  Select,
  SelectItem,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Checkbox } from "components/ui/checkbox";
import { Label } from "components/ui/label";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Loader } from "lucide-react";
import { useAuth } from "AuthContext";
import { digikeyCreateOrder, getAccountInfo } from "actions/digikeyAction";
import { PRIMARY_APPLICATIONS, SECONDARY_APPLICATIONS } from "constants/common";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { saveOrder } from "actions/cartActions";
import { warnIfHubSyncFailed } from "utils/hubSyncWarning";
import { getProductPrice } from "utils/cartUtils";
import SamiSpin from "components/SamiSpin";
const MySwal = withReactContent(Swal);

const DIGIKEY_DEFAULT_SHIP_METHOD_VALUE = "DHL worldwide Express";

const splitName = (fullName) => {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

const DIGIKEY_SHIPPING_METHODS = [
  "DHL worldwide Express",
  "FedEx ground",
  "FedEx 3 day",
  "FedEx 2 day",
  "FedEx standard overnight",
  "FedEx priority overnight",
  "FedEx first overnight",
  "FedEx international priority",
  "FedEx international economy",
  "UPS ground",
  "UPS 3 day",
  "UPS 2 day",
  "UPS 2 day noon",
  "UPS 1 day",
  "UPS 1 day 3 p.m.",
  "UPS 1 day early a.m.",
  "UPS worldwide expedited",
  "UPS worldwide express",
  "UPS worldwide saver",
  "USPS express mail international",
  "USPS express mail",
  "USPS priority mail",
  "USPS ground advantage",
  "USPS standard mail",
];

const DigikeyModal = ({ open, setModelData, modelData }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [poNumber, setPoNumber] = useState("");
  const [currency, setCurrency] = useState("INR");
  const { cart, removeByPartNumber } = useAuth();
  const { user } = useSelector((state) => state.user);
  const digikeyCartItems = cart.filter(
    (item) => item.api_partner.toLowerCase() === "digikey",
  );
  // console.log({ digikeyCartItems });

  const [data, setData] = useState({
    sameAsShippingAddress: false,
    BillingAccount: "",
    ShippingMethod: DIGIKEY_DEFAULT_SHIP_METHOD_VALUE,
    ExportQuestions: {},
    ShippingContact: {},
    BuyerContact: {},
  });

  const onLoad = async () => {
    try {
      const response = await getAccountInfo();
      console.log("response=>", response);
      if (response.status) {
        const responseData = response?.data;
        console.log("responseData=>", responseData);

        const shippingName = splitName(
          responseData?.shipping_address?.shipping_first_name
            ? `${responseData?.shipping_address?.shipping_first_name || ""} ${responseData?.shipping_address?.shipping_last_name || ""}`
            : responseData?.name,
        );
        const buyerName = splitName(
          responseData?.buyer_address?.buyer_first_name
            ? `${responseData?.buyer_address?.buyer_first_name || ""} ${responseData?.buyer_address?.buyer_last_name || ""}`
            : responseData?.name,
        );

        setData((prev) => ({
          ...prev,
          BillingAccount: responseData?.billing_account
            ? parseInt(responseData.billing_account)
            : prev.BillingAccount,
          ShippingMethod:
            prev.ShippingMethod || DIGIKEY_DEFAULT_SHIP_METHOD_VALUE,
          ShippingContact: {
            Name: responseData?.name || "",
            Telephone: responseData?.telephone || "",
            CustomerId: responseData?.customer_id || "",
            Address: {
              Company: responseData?.shipping_address?.shipping_company || "",
              FirstName:
                responseData?.shipping_address?.shipping_first_name ||
                shippingName.firstName,
              LastName:
                responseData?.shipping_address?.shipping_last_name ||
                shippingName.lastName,
              Email: responseData?.shipping_address?.shipping_email || "",
              AddressLineOne:
                responseData?.shipping_address?.shipping_address_line_one ||
                responseData?.shipping_address?.shipping_address_line_two ||
                "",
              AddressLineTwo:
                responseData?.shipping_address?.shipping_address_line_two || "",
              City: responseData?.shipping_address?.shipping_city || "",
              Province: responseData?.shipping_address?.shipping_province || "",
              PostalCode:
                responseData?.shipping_address?.shipping_postal_code || "",
              Country: responseData?.shipping_address?.shipping_country || "",
            },
          },
          BuyerContact: {
            Name: responseData?.name || "",
            Telephone: responseData?.telephone || "",
            CustomerId: responseData?.customer_id || "",
            Address: {
              Company: responseData?.buyer_address?.buyer_company || "",
              FirstName:
                responseData?.buyer_address?.buyer_first_name ||
                buyerName.firstName,
              LastName:
                responseData?.buyer_address?.buyer_last_name ||
                buyerName.lastName,
              Email: responseData?.buyer_address?.buyer_email || "",
              AddressLineOne:
                responseData?.buyer_address?.buyer_address_line_one ||
                responseData?.buyer_address?.buyer_address_line_two ||
                "",
              AddressLineTwo:
                responseData?.buyer_address?.buyer_address_line_two || "",
              City: responseData?.buyer_address?.buyer_city || "",
              Province: responseData?.buyer_address?.buyer_province || "",
              PostalCode: responseData?.buyer_address?.buyer_postal_code || "",
              Country: responseData?.buyer_address?.buyer_country || "",
            },
          },
          ExportQuestions: {
            CountryCode: responseData?.export_questions?.country_code || "",
            ConsigneeName: responseData?.export_questions?.consignee_name || "",
            PrimaryApplication:
              responseData?.export_questions?.primary_application || "",
            SecondaryApplication:
              responseData?.export_questions?.secondary_application || "",
            AdditionalInfo:
              responseData?.export_questions?.additional_info || "",
          },
        }));
      }
    } catch (error) {
      console.error("Error loading account info:", error);
    }
  };

  // const handleChange = (e, contactType) => {
  //   const { name, value } = e.target;
  //   // Address fields that are nested inside Address object
  //   const addressFields = [
  //     "Company",
  //     "FirstName",
  //     "LastName",
  //     "Email",
  //     "AddressLineOne",
  //     "AddressLineTwo",
  //     "City",
  //     "Province",
  //     "PostalCode",
  //     "Country"
  //   ];

  //   if (contactType === "ShippingContact" || contactType === "shippingAddress") {
  //     if (addressFields.includes(name)) {
  //       setData({
  //         ...data,
  //         ShippingContact: {
  //           ...data.ShippingContact,
  //           Address: { ...data.ShippingContact.Address, [name]: value }
  //         }
  //       });
  //     } else {
  //       // Handle top-level fields like Name, Telephone, CustomerId
  //       setData({
  //         ...data,
  //         ShippingContact: {
  //           ...data.ShippingContact,
  //           [name]: value
  //         }
  //       });
  //     }
  //   } else if (contactType === "BuyerContact") {
  //     if (addressFields.includes(name)) {
  //       setData({
  //         ...data,
  //         BuyerContact: {
  //           ...data.BuyerContact,
  //           Address: { ...data.BuyerContact.Address, [name]: value }
  //         }
  //       });
  //     } else {
  //       // Handle top-level fields like Name, Telephone, CustomerId
  //       setData({
  //         ...data,
  //         BuyerContact: {
  //           ...data.BuyerContact,
  //           [name]: value
  //         }
  //       });
  //     }
  //   } else {
  //     setData({
  //       ...data,
  //       [name]: value
  //     });
  //   }
  // };

  // const handleCheckboxChange = (checked) => {
  //   console.log("checked", checked);
  //   if (checked) {
  //     setData({
  //       ...data,
  //       BuyerContact: {
  //         ...data.ShippingContact
  //       },
  //       sameAsShippingAddress: checked
  //     });
  //   } else {
  //     setData({
  //       ...data,
  //       BuyerContact: {
  //         Name: "",
  //         Telephone: "",
  //         CustomerId: "",
  //         Address: {
  //           Company: "",
  //           FirstName: "",
  //           LastName: "",
  //           Email: "",
  //           AddressLineOne: "",
  //           AddressLineTwo: "",
  //           City: "",
  //           Province: "",
  //           PostalCode: "",
  //           Country: ""
  //         }
  //       },
  //       sameAsShippingAddress: checked
  //     });
  //   }
  // };

  const handleApplicationChange = (newValue) => {
    console.log("primary newValue", newValue);
    setData((prev) => ({
      ...prev,
      ExportQuestions: {
        ...prev.ExportQuestions,
        PrimaryApplication: newValue,
      },
    }));
  };

  const handleSecondaryApplicationChange = (newValue) => {
    console.log("secondary newValue", newValue);
    setData((prev) => ({
      ...prev,
      ExportQuestions: {
        ...prev.ExportQuestions,
        SecondaryApplication: newValue,
      },
    }));
  };

  const handleClose = () => {
    console.log("handleClose", { modelData, setModelData });
    setModelData({ ...modelData, open: false });
    setError(null);
  };

  const handleOpenChange = (open) => {
    console.log("handleOpenChange", open);
    setModelData({ ...modelData, open });
    if (!open) {
      setError(null);
    }
  };

  useEffect(() => {
    if (open) {
      onLoad();
    }
  }, [open]);

  console.log({ data });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError(null);
      console.log("Submit", { data });

      // Required-field validation before submitting the order.
      const requiredFields = [
        { label: "Shipping First Name", value: data?.ShippingContact?.Address?.FirstName },
        { label: "Shipping Last Name", value: data?.ShippingContact?.Address?.LastName },
        { label: "Billing First Name", value: data?.BuyerContact?.Address?.FirstName },
        { label: "Billing Last Name", value: data?.BuyerContact?.Address?.LastName },
        { label: "Customer ID", value: data?.ShippingContact?.CustomerId },
      ];
      const missing = requiredFields
        .filter((f) => !String(f.value ?? "").trim())
        .map((f) => f.label);
      if (missing.length > 0) {
        setError(`Please fill the following required field(s): ${missing.join(", ")}`);
        setIsLoading(false);
        return;
      }

      const shippingName =
        data?.ShippingContact?.Name ||
        data?.ShippingContact?.Address?.Company ||
        user?.company_name ||
        user?.name ||
        "";
      const buyerName =
        data?.BuyerContact?.Name ||
        data?.BuyerContact?.Address?.Company ||
        user?.company_name ||
        user?.name ||
        "";
      const payload = {
        PurchaseOrderNumber: poNumber.trim() ? poNumber.trim() : `PO-${Date.now()}`,
        Currency: currency,
        ShipControl: "Immediate",
        MfgCOfCRequired: true,
        ShippingContact: {
          ...data.ShippingContact,
          Name: shippingName,
        },
        BuyerContact: {
          ...data.BuyerContact,
          Name: buyerName,
        },
        ShipperAccountNumber: "",
        LineItems: digikeyCartItems.map((item, index) => {
          return {
            MfgCOfCRequired: true,
            CustomerLineItemId: `${index + 1}`,
            ProductDescription: "",
            CustomerPartNumber: "",
            DigiKeyPartNumber: item.api_partner_part_number,
            ManufacturerPartNumber: item.part_number,
            ManufacturerName: item.manufacturer,
            RequestedQuantity: item.required_qty,
            Quantity: item.required_qty,
            UnitPrice: getProductPrice(item),
            QuoteId: item?.quoteId || 1,
            DeliveryDate: "",
            // LineItemNotes: item.notes || [],
            // Schedules: item.schedules || [],
          };
        }),
        OrderNotes: [],
        PackagingPreference: "CT",
        Taxable: true,
        ExportQuestions: {
          ...data.ExportQuestions,
          CountryCode: data?.ShippingContact?.Address?.Country || "",
          ConsigneeName: shippingName,
        },
      };

      const billingAccount = parseInt(data?.BillingAccount, 10);
      if (Number.isInteger(billingAccount)) {
        payload.BillingAccount = billingAccount;
      }

      if (data?.ShippingMethod) {
        payload.ShipMethod = data.ShippingMethod;
      }
      console.log({ payload });

      const res = await digikeyCreateOrder({ payload });
      console.log({ res });

      if (res.status) {
        const bomLinks = digikeyCartItems
          .filter((item) => item.bom_item_id)
          .map((item) => ({
            bom_item_id: item.bom_item_id,
            bom_id: item.bom_id ?? null,
            mfr_part_number: item.part_number ?? null,
            part_number: item.api_partner_part_number ?? null,
          }));
        const saveOrderResponse = await saveOrder({
          digikey_data: {
            ...res.data,
            cartDetailItems: digikeyCartItems,
          },
          bom_links: bomLinks,
        });
        console.log("saveOrderResponse=>", saveOrderResponse);
        warnIfHubSyncFailed(saveOrderResponse?.data?.hub_sync);
        handleClose();
        // Remove digikey cart items from the cart
        digikeyCartItems.forEach((item) => {
          removeByPartNumber(item.part_number);
        });
        await MySwal.fire({
          title: <strong>Digikey Order Success !</strong>,
          html: (
            <i>
              Order created successfully, Your order number is{" "}
              <b>{res.data.SalesOrderId}</b>
            </i>
          ),
          icon: "success",
        });
        navigate("/orders");
      } else {
        setError(res.message);
      }
    } catch (error) {
      console.log({ error });
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddressFieldChange = (contactType, field, value) => {
    setData((prev) => ({
      ...prev,
      [contactType]: {
        ...prev[contactType],
        Address: {
          ...prev[contactType]?.Address,
          [field]: value,
        },
      },
    }));
  };

  const handleCustomerIdChange = (value) => {
    const digitsOnly = String(value ?? "").replace(/\D/g, "");
    setData((prev) => ({
      ...prev,
      ShippingContact: { ...prev.ShippingContact, CustomerId: digitsOnly },
      BuyerContact: { ...prev.BuyerContact, CustomerId: digitsOnly },
    }));
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Shipping Address Section */}
          <div className="mb-6">
            <DialogHeader className="mb-4">
              <DialogTitle className="text-primary text-lg font-semibold">
                Shipping Address
              </DialogTitle>
            </DialogHeader>
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                <div className="flex flex-col">
                  <Label htmlFor="shippingFirstName" className="text-sm text-gray-600 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </Label>
                  <input
                    id="shippingFirstName"
                    type="text"
                    value={data?.ShippingContact?.Address?.FirstName || ""}
                    onChange={(e) =>
                      handleAddressFieldChange("ShippingContact", "FirstName", e.target.value)
                    }
                    placeholder="Enter first name"
                    className="w-full h-10 px-3 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-s360-violet/20"
                  />
                </div>
                <div className="flex flex-col">
                  <Label htmlFor="shippingLastName" className="text-sm text-gray-600 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </Label>
                  <input
                    id="shippingLastName"
                    type="text"
                    value={data?.ShippingContact?.Address?.LastName || ""}
                    onChange={(e) =>
                      handleAddressFieldChange("ShippingContact", "LastName", e.target.value)
                    }
                    placeholder="Enter last name"
                    className="w-full h-10 px-3 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-s360-violet/20"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">
                    Company:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.ShippingContact?.Address?.Company || "-"}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">
                    Email:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.ShippingContact?.Address?.Email || "-"}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">
                    Address Line 1:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.ShippingContact?.Address?.AddressLineOne || "-"}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">
                    Address Line 2:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.ShippingContact?.Address?.AddressLineTwo || "-"}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">
                    City:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.ShippingContact?.Address?.City || "-"}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">
                    Province:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.ShippingContact?.Address?.Province || "-"}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">
                    Postal Code:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.ShippingContact?.Address?.PostalCode || "-"}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">
                    Country:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.ShippingContact?.Address?.Country || "-"}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col md:col-span-2">
                  <span className="text-sm text-gray-600 mb-1">
                    Phone:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.ShippingContact?.Telephone || "-"}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Billing Address Section */}
          <div className="mb-6">
            <DialogHeader className="mb-4">
              <DialogTitle className="text-primary text-lg font-semibold">
                Billing Address
              </DialogTitle>
            </DialogHeader>
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                <div className="flex flex-col">
                  <Label htmlFor="billingFirstName" className="text-sm text-gray-600 mb-1">
                    First Name <span className="text-red-500">*</span>
                  </Label>
                  <input
                    id="billingFirstName"
                    type="text"
                    value={data?.BuyerContact?.Address?.FirstName || ""}
                    onChange={(e) =>
                      handleAddressFieldChange("BuyerContact", "FirstName", e.target.value)
                    }
                    placeholder="Enter first name"
                    className="w-full h-10 px-3 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-s360-violet/20"
                  />
                </div>
                <div className="flex flex-col">
                  <Label htmlFor="billingLastName" className="text-sm text-gray-600 mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </Label>
                  <input
                    id="billingLastName"
                    type="text"
                    value={data?.BuyerContact?.Address?.LastName || ""}
                    onChange={(e) =>
                      handleAddressFieldChange("BuyerContact", "LastName", e.target.value)
                    }
                    placeholder="Enter last name"
                    className="w-full h-10 px-3 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-s360-violet/20"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">
                    Company:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.BuyerContact?.Address?.Company || "-"}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">
                    Email:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.BuyerContact?.Address?.Email || "-"}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">
                    Address Line 1:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.BuyerContact?.Address?.AddressLineOne || "-"}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">
                    Address Line 2:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.BuyerContact?.Address?.AddressLineTwo || "-"}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">
                    City:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.BuyerContact?.Address?.City || "-"}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">
                    Province:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.BuyerContact?.Address?.Province || "-"}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">
                    Postal Code:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.BuyerContact?.Address?.PostalCode || "-"}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-600 mb-1">
                    Country:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.BuyerContact?.Address?.Country || "-"}
                    </span>
                  </span>
                </div>
                <div className="flex flex-col md:col-span-2">
                  <span className="text-sm text-gray-600 mb-1">
                    Phone:{" "}
                    <span className="text-base font-semibold text-gray-900">
                      {data?.BuyerContact?.Telephone || "-"}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div>
            <Label htmlFor="shippingMethod">
              Please select the Primary and Secondary Application
            </Label>
          </div>
          <div className="grid grid-cols-2 gap-6 py-4">
            <div className="space-y-2">
              <Select
                name="PrimaryApplication"
                placeholder="Primary Application"
                value={data?.ExportQuestions?.PrimaryApplication}
                onValueChange={handleApplicationChange}
                required
              >
                <SelectTrigger className="col-span-3 bg-gray-50 focus-visible:ring-ring focus-visible:ring-0 focus-visible:ring-offset-0">
                  <SelectValue placeholder="Select Primary Application" />
                </SelectTrigger>
                <SelectContent>
                  {PRIMARY_APPLICATIONS.map((application) => (
                    <SelectItem
                      key={application.value}
                      value={application.value}
                    >
                      {application.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Select
                name="PrimaryApplication"
                placeholder="Secondary Application"
                value={data?.ExportQuestions?.SecondaryApplication}
                onValueChange={handleSecondaryApplicationChange}
                required
              >
                <SelectTrigger className="col-span-3 bg-gray-50 focus-visible:ring-ring focus-visible:ring-0 focus-visible:ring-offset-0">
                  <SelectValue placeholder="Select Secondary Application" />
                </SelectTrigger>
                <SelectContent>
                  {data?.ExportQuestions?.PrimaryApplication &&
                    SECONDARY_APPLICATIONS[
                      data?.ExportQuestions?.PrimaryApplication
                    ]?.map((application) => (
                      <SelectItem
                        key={application.value}
                        value={application.value}
                      >
                        {application.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="shippingMethod">
                Please select the Shipping Method
              </Label>
              <Select
                name="shippingMethod"
                placeholder="Select Shipping Method"
                value={data?.ShippingMethod}
                onValueChange={(value) =>
                  setData({ ...data, ShippingMethod: value })
                }
                required
              >
                <SelectTrigger className="col-span-3 bg-gray-50 focus-visible:ring-ring focus-visible:ring-0 focus-visible:ring-offset-0">
                  <SelectValue placeholder="Select Shipping Method" />
                </SelectTrigger>
                <SelectContent>
                  {DIGIKEY_SHIPPING_METHODS.map((method) => (
                    <SelectItem key={method} value={method}>
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="poNumber">
                PO Number <span className="text-gray-400 font-normal">(optional)</span>
              </Label>
              <input
                id="poNumber"
                type="text"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                placeholder={`PO-${Date.now()}`}
                className="w-full h-10 px-3 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-s360-violet/20"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customerId">
                Customer ID (DigiKey Account ID) <span className="text-red-500">*</span>
              </Label>
              <input
                id="customerId"
                type="text"
                inputMode="numeric"
                value={data?.ShippingContact?.CustomerId || ""}
                onChange={(e) => handleCustomerIdChange(e.target.value)}
                placeholder="Enter your DigiKey Customer/Account ID"
                className="w-full h-10 px-3 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-s360-violet/20"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="col-span-3 bg-gray-50 focus-visible:ring-ring focus-visible:ring-0 focus-visible:ring-offset-0">
                  <SelectValue placeholder="Select Currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {error && (
            <div className="text-sm mt-3 mb-4 text-red-500">
              Error: <span className="text-red-500">{error}</span>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="secondary"
              className="hover:bg-gray-300 border border-gray-300"
              type="button"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isLoading}>
              Checkout{" "}
              {isLoading && <SamiSpin className="w-4 h-4 animate-spin" />}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DigikeyModal;
